import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { convertCurrencyHelper } from '../../../../helpers/forexConvertor';
import { calculateTotalExpense } from './Services/reportExpense';

// Functional component
function TotalExpense({ expenseData }) {
  const [displayedExpense, setDisplayedExpense] = useState(0);
  const [targetExpense, setTargetExpense] = useState(0);
  const animationFrameRef = useRef(null);
  const reportingFinancialExpense = useSelector((state) => state.reportingExpense);
  const { forex } = useSelector((state) => state.forex);
  const settings = JSON.parse(localStorage.getItem("settings"));

  const setReportingCurrency = useSelector((state) => state.reportingExpense.expCurrency);
  var currencies = "USD";
  if (setReportingCurrency && setReportingCurrency.length === 1) {
    currencies = setReportingCurrency[0];
  } else {
    currencies = settings?.currency || "USD";
  }

  useEffect(() => {
    if (expenseData && reportingFinancialExpense && currencies && forex) {
      const totalExpense = calculateTotalExpense(expenseData, reportingFinancialExpense, currencies, forex);
      const parsedExpense = parseFloat(totalExpense) || 0;
      setTargetExpense(parsedExpense);
    } else {
      setTargetExpense(0);
    }
  }, [expenseData, reportingFinancialExpense, currencies, forex]);

  useEffect(() => {
    // Cancel any existing animation
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }

    const startValue = displayedExpense;
    const endValue = targetExpense;
    const duration = 1000; // Animation duration in milliseconds
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smooth animation (ease-out)
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      const currentValue = startValue + (endValue - startValue) * easeOut;
      setDisplayedExpense(parseFloat(currentValue.toFixed(2)));

      if (progress < 1) {
        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayedExpense(endValue);
        animationFrameRef.current = null;
      }
    };

    if (Math.abs(startValue - endValue) > 0.01) {
      animationFrameRef.current = requestAnimationFrame(animate);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [targetExpense]);

  return (
    <div>
      <div className="overview-item">
        <div className="overview-title-outer">
          <div className="overview-title">
            Total Expenses
          </div>
          <div className="overview-icon">
            <svg width="49" height="49" viewBox="0 0 49 49" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g filter="url(#filter0_d_2991_23)">
                <circle cx="24.5" cy="24.5" r="20.5" fill="white" />
              </g>
              <path d="M17.5 21C16.673 21 16 21.673 16 22.5V23.5C16 24.327 16.673 25 17.5 25H22.5C23.327 25 24 24.327 24 23.5V22.5C24 21.673 23.327 21 22.5 21H17.5ZM23 22.5V23.5C23 23.775 22.775 24 22.5 24H17.5C17.225 24 17 23.775 17 23.5V22.5C17 22.225 17.225 22 17.5 22H22.5C22.775 22 23 22.225 23 22.5ZM23 27.5C23 27.776 22.776 28 22.5 28H21.5C21.224 28 21 27.776 21 27.5C21 27.224 21.224 27 21.5 27H22.5C22.776 27 23 27.224 23 27.5ZM23 30.5C23 30.776 22.776 31 22.5 31H21.5C21.224 31 21 30.776 21 30.5C21 30.224 21.224 30 21.5 30H22.5C22.776 30 23 30.224 23 30.5ZM19 27.5C19 27.776 18.776 28 18.5 28H17.5C17.224 28 17 27.776 17 27.5C17 27.224 17.224 27 17.5 27H18.5C18.776 27 19 27.224 19 27.5ZM19 30.5C19 30.776 18.776 31 18.5 31H17.5C17.224 31 17 30.776 17 30.5C17 30.224 17.224 30 17.5 30H18.5C18.776 30 19 30.224 19 30.5ZM23 33.5C23 33.776 22.776 34 22.5 34H21.5C21.224 34 21 33.776 21 33.5C21 33.224 21.224 33 21.5 33H22.5C22.776 33 23 33.224 23 33.5ZM19 33.5C19 33.776 18.776 34 18.5 34H17.5C17.224 34 17 33.776 17 33.5C17 33.224 17.224 33 17.5 33H18.5C18.776 33 19 33.224 19 33.5ZM30.487 13H20.5C17.468 13 15 15.468 15 18.5C15 18.518 15.008 18.533 15.01 18.551C13.815 19.243 13 20.522 13 22V33C13 35.206 14.794 37 17 37H23C25.206 37 27 35.206 27 33V22C27 20.547 26.214 19.286 25.053 18.585C25.503 15.929 27.788 14 30.487 14C33.527 14 36 16.473 36 19.513V28.51L33.964 27.009C33.793 26.881 33.559 26.878 33.382 27.001L30.833 28.772L29.785 28.043C29.559 27.887 29.247 27.94 29.09 28.168C28.932 28.395 28.987 28.706 29.215 28.863L30.548 29.791C30.718 29.91 30.946 29.91 31.118 29.791L33.658 28.025L36.203 29.901C36.291 29.965 36.395 29.999 36.5 29.999C36.577 29.999 36.654 29.981 36.726 29.945C36.894 29.86 37 29.687 37 29.499V19.513C37 15.922 34.078 13 30.487 13ZM26 22V33C26 34.654 24.654 36 23 36H17C15.346 36 14 34.654 14 33V22C14 20.346 15.346 19 17 19H23C24.654 19 26 20.346 26 22ZM24.129 18.182C23.769 18.075 23.395 18 23 18H17C16.666 18 16.347 18.053 16.037 18.13C16.229 15.824 18.145 14 20.5 14H27.035C25.571 14.915 24.5 16.397 24.129 18.182Z" fill="#6A7988" stroke="#6A7988" strokeWidth="0.2" />
              <defs>
                <filter id="filter0_d_2991_23" x="0" y="0" width="49" height="49" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                  <feFlood floodOpacity="0" result="BackgroundImageFix" />
                  <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                  <feOffset />
                  <feGaussianBlur stdDeviation="2" />
                  <feComposite in2="hardAlpha" operator="out" />
                  <feColorMatrix type="matrix" values="0 0 0 0 0.468733 0 0 0 0 0.51145 0 0 0 0 0.554167 0 0 0 0.39 0" />
                  <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_2991_23" />
                  <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_2991_23" result="shape" />
                </filter>
              </defs>
            </svg>

          </div>
        </div>

        <div className="overview-rice">
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: currencies,
          }).format(displayedExpense)}
        </div>
      </div>

    </div>
  );
}

export default TotalExpense;
